import * as Yup from "yup";
import moment from "moment";
import { useState, useEffect, useLayoutEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { API_URL, RETURN_TRANSACTION_METHOD_OPTION } from "../../../constants";
import { useFormikContext } from "formik";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import { FormikDialog } from "../../../components/dialogs";
import FormikReferenceField from "../../../components/inputs/FormikReferenceField";
import FormikDateTimeField from "../../../components/inputs/FormikDateTimeField";
import FormikSelectField from "../../../components/inputs/FormikSelectField";
import FormikTextField from "../../../components/inputs/FormikTextField";
import axios from "axios";
import InputAdornment from "@material-ui/core/InputAdornment";

export const initialValues = {
  amount: "",
  amountConfirmation: "",
  description: "",
  status: "RETURNED_DEPOSIT",
  method: "",
  remarks: "",
  issueDateTime: moment(new Date()).toISOString(),
};

export const validationSchema = Yup.object().shape({
  method: Yup.string()
    .required(<FormattedMessage id="validation.message.required" />)
    .nullable(),
  issueDateTime: Yup.date()
    .required(<FormattedMessage id="validation.message.required" />)
    .typeError(<FormattedMessage id="validation.message.invalidDateFormat" />)
    .nullable(),
  amount: Yup.string()
    .matches(/^(\-|\+)?\d+(\.\d+)?$/, "輸入必須為數字")
    .required(<FormattedMessage id="validation.message.required" />)
    .nullable(),
  amountConfirmation: Yup.string().when("amount", (value, schema) => {
    if (!value) {
      return schema.notRequired();
    }
    return schema
      .oneOf([Yup.ref("amount")], "確認退還金額與退還金額不符")
      .required(<FormattedMessage id="validation.message.required" />);
  }),
});

export function ReturnDepositDialog(props) {
  const { open, entity, onClose, onSubmit, apiUrl, normalizeReceivedValues } =
    props;
  const [entityValues, setEntityValues] = useState();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (entity) {
      axios
        .get((apiUrl.getOne ? apiUrl.getOne : apiUrl) + "/" + entity.id)
        .then((response) => {
          let data = response?.data;
          let entityValues = { ...initialValues, ...data };
          normalizeReceivedValues(entityValues);
          setEntityValues(entityValues);
        })
        .catch((error) => {})
        .finally(() => {
          setLoaded(true);
        });
    } else {
      setEntityValues(initialValues);
    }
  }, [entity]);

  return (
    <FormikDialog
      open={open}
      dialogTitle={"退還按金 (" + entityValues?.invoiceId + ")"}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth="sm"
    >
      <PaymentForm entity={entityValues} />
    </FormikDialog>
  );
}

const PaymentForm = (props) => {
  const home = useSelector((state) => state.home);
  const { entity, apiUrl = "" } = props;
  const { initialValues, values, touched, errors, setFieldValue, resetForm } =
    useFormikContext();

  useLayoutEffect(() => {
    resetForm({ values: initialValues });
    if (entity) {
      resetForm({ values: { ...initialValues, ...entity } });
    } else {
      resetForm({ values: initialValues });
    }
  }, [entity]);

  useEffect(() => {}, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item sm={12} xs={12}>
          <Card variant="outlined" style={{ marginBottom: 15 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                按金資料
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <FormikReferenceField
                    edit={entity ? true : false}
                    disabled
                    required
                    label="院友姓名"
                    name="resident"
                    apiUrl={
                      "/api/homes/" + home.home.id + "/residents/references"
                    }
                    getOptionLabel={(option) =>
                      option.lastnameTc + option.firstnameTc
                    }
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="按金編號"
                    name="invoiceId"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikDateTimeField
                    disabled
                    required
                    label="按金日期及時間"
                    value={entity.issueDateTime}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="按金金額"
                    name="total"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card variant="outlined" style={{ marginBottom: 15 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                退還資料
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    required
                    type="number"
                    label="退還金額"
                    name="amount"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    required
                    type="number"
                    label="確認退還金額"
                    name="amountConfirmation"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikSelectField
                    required
                    label="退還方法"
                    name="method"
                    options={RETURN_TRANSACTION_METHOD_OPTION}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikDateTimeField
                    required
                    label="退還日期及時間"
                    name="issueDateTime"
                  />
                </Grid>
                <Grid item sm={12} xs={12}>
                  <FormikTextField label="描述" name="description" />
                </Grid>
                <Grid item sm={12} xs={12}>
                  <FormikTextField label="退還備註" name="remarks" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </form>
  );
};
